/**
 * HIPAA dashboard — RDS <-> Supabase parity API client.
 *
 * The parity report is the answer to "does RDS still hold exactly what
 * Supabase holds?". It is computed on the NotesAI backend (api/rds_parity.py)
 * because only the backend can read both sides: Supabase over read-only SQL
 * and RDS over its admin HTTP routes.
 *
 * The call is slower than a health poll — it counts rows and reads every
 * mirrored table's shape on both sides — so it has its own timeout and is
 * never run automatically. The operator asks for it.
 */
import { getBackendURL } from "../components/env";
import { supabasePublic } from "../components/supabaseClient";

/** The parity check reads every mirrored table on both sides; give it room. */
export const PARITY_TIMEOUT_MS = 180000;

export async function fetchRdsParity({ timeoutMs = PARITY_TIMEOUT_MS } = {}) {
  const { data: { session } = {} } = await supabasePublic().auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("No active session — sign in again");

  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const res = await fetch(`${getBackendURL()}/api/admin/rds-parity/`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
      signal: controller ? controller.signal : undefined,
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.error) {
      throw new Error(json.error || `HTTP ${res.status}`);
    }
    return json;
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new Error("The comparison took too long and was stopped.");
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
